import { useTranslations } from '@vocab/react';
import { Box, Stack, TextLink, useToast } from 'braid-design-system';
import { observer } from 'mobx-react';
import { type FC, useState, useEffect, useRef, useMemo } from 'react';
import loadable from 'sku/@loadable/component';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';

import AppErrorBoundary from 'src/components/app/AppErrorBoundary';
import { useFilters } from 'src/components/app/FilterSideBar/useFilters';
import JobSection from 'src/components/app/JobSection';
import JobActionMenu from 'src/components/app/JobSection/JobActionMenu';
import {
  BillableApplicantsLink,
  JobExpireDate,
  JobStatDetailLayout,
  ReferenceCheckBalanceLink,
  TotalApplications,
} from 'src/components/app/JobSection/JobStatDetail';
import { JobTitleHeadingContent } from 'src/components/app/JobSection/JobTitleHeadingContent';
import PendingAlert from 'src/components/app/JobSection/PendingAlert';
import { useUserActionStatus } from 'src/components/common/UserActionStatusContext';
import CandidateList from 'src/components/list/index';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import {
  HOTJAR_ORDER_REFERENCE_CHECK_BUTTON_CLICKED,
  HOTJAR_BUDGETS_LINK_BUTTON_CLICKED,
  HOTJAR_VIEW_REFERENCE_CHECK_BUTTON_CLICKED,
  HOTJAR_TAG_BULK_ACTIONS,
  HOTJAR_TAG_SORT,
  HOTJAR_UPGRADE_REFERENCES_BUTTON_CLICKED,
} from 'src/constants/hotjar';
import { getDashisedBucket } from 'src/constants/jobApplicationStatus';
import { BULK_MOVE_LIMIT } from 'src/constants/numPerPage';
import {
  mapBucketsToAnalyticsApplicationStatus,
  type CheckReferencesActionOrigins,
  type TrackingCurrentPage,
} from 'src/constants/pageTrackingData';
import { JOB_APPLICATION_TAB } from 'src/constants/tabs';
import { TrackOnMount } from 'src/libs/Analytics';
import { Display } from 'src/libs/Display';
import type { PageConfig } from 'src/libs/PageConfig';
import { UnlockReferenceCheckDialog } from 'src/libs/ReferenceChecks/UnlockReferenceCheckDialog';
import { TextSkeleton } from 'src/libs/TextSkeleton';
import { IntercomConfiguration } from 'src/modules/intercom/IntercomConfiguration';
import {
  PAGE_QUERY_PARAM_KEY,
  PAGE_TAB_PARAM_KEY,
} from 'src/pages/CandidateManagementPage/queryParams';
import { useDeepLinkedDetailView } from 'src/pages/CandidateManagementPage/utils/useDeepLinkedDetailView';
import {
  buildFiltersData,
  type FiltersEventCaptureData,
} from 'src/services/jobApplicationService/buildCaptureEventData';
import { sendDetailView } from 'src/services/jobApplicationService/sendDetailView';
import {
  BUCKETS,
  type HighFitApplicationData,
  type HighFitExperimentVariant,
} from 'src/stores/JobApplicationStore/types';
import { getReferenceBalance } from 'src/stores/JobStore/utils/getReferenceBalance';
import { SUPPORT_BACK_BUTTON } from 'src/utils/buildHandleEnterAndSpaceKeyPress';
import {
  trackBulkActionsEmailTabClicked,
  trackBulkActionsMoveTabClicked,
  trackCandidateCardStatus,
  trackClickBillableApplicantsDefinition,
  trackFreemiumButtonDisplay,
  trackFreemiumButtonPressedEvent,
  trackSortOptionChangedEvent,
  trackUpdateStatusMultiple,
} from 'src/utils/events';
import useQueryParamsStore from 'src/utils/hooks/useQueryParamsStore';
import { useStores } from 'src/utils/hooks/useStores';
import { sendHotjarTag } from 'src/utils/hotjar';
import { sleep } from 'src/utils/sleep';

import { MobileFilterSection } from '../../components/app/MobileFilterSection';
import UnhandledErrorPageBlock from '../../components/app/UnhandledErrorPageBlock';
import { getSignInUrl } from '../../pages/CandidateManagementPage/utils/getSignInUrl';
import type { IJobStore } from '../../stores/JobStore';
import type { IQuestionnaireStore } from '../../stores/JobStore/QuestionnaireStore';
import { UserLoadState } from '../../stores/UserStore/types';
import { JobPlacementDialog } from '../CandidateManagementPage/JobPlacementDialog';
import { type PageTab, PageTabs } from '../CandidateManagementPage/PageTabs';
import { RelevantApplicationDialog } from '../CandidateManagementPage/RelevantApplicationDialog';
import useHandleStatusChange from '../hooks/useHandleStatusChange';
import { useHighFitError } from '../hooks/useHighFitError';

import translations from './.vocab';
import { ActionToolbar } from './ActionToolbar';
import { useApplicationsFacetsQuery } from './AppPageBlock.hirer.generated';
import { AppPageLayout } from './AppPageLayout';
import { AutoRejectBanner } from './AutoRejectBanner';
import { FilterDesktop } from './FilterDesktop';
import { FilterDrawerMobile } from './FilterDrawerMobile';
import { FilterLocation } from './FilterLocation';
import { FilterNationality } from './FilterNationality';
import {
  FilterScreeningQuestion,
  type FilterScreeningQuestionProps,
} from './FilterScreeningQuestion';
import { StatusFolderButtons } from './FilterStatusFolders';
import { FilterTabletDesktopContentLayout } from './FilterTabletDesktopContentLayout';
import { FreemiumButton } from './FreemiumButton';
import {
  FreemiumHardToFillModal,
  FreemiumUrgentHireModal,
} from './FreemiumModal';
import { HighFitErrorMessage } from './HighFitErrorMessage';
import { MobileStickyHeader } from './MobileStickyHeader';
import NotificationPreferenceDialog from './NotificationPreferenceDialog/NotificationPreferenceDialog';
import { OrderReferenceCheckDialog } from './OrderReferenceCheckDialog';
import { PageAlert } from './PageAlert';
import { PageBlock } from './PageBlock';
import {
  type JobPlacementStatus,
  PlacementStatusLink,
} from './PlacementStatusLink';
import {
  PurchaseReferenceCheckAddOnDialog,
  PurchaseReferenceCheckErrorDialog,
} from './PurchaseReferenceCheckAddOnDialog';
import { SearchResultSummary } from './SearchResultSummary';
import type { CandidatesManagementPageAnalytics } from './createPageAnalytics';
import type {
  ApplicationStatusFolder,
  QuestionnaireFilter,
  ResponseErrorReason,
} from './graphql/hirer-types.generated';
import { getQuestionnaireAnswerFacetCount } from './utils/getQuestionnaireAnswerFacetCount';
import { getJobReferenceCheckTrackingProps } from './utils/getReferenceCheckTrackingProps';
import {
  type ApplicationStatusFacets,
  getStatusFacetsFromQueryData,
} from './utils/getStatusFacetsFromQueryData';

const HirerJobActivity = loadable(
  () => import('src/components/app/HirerActivity/HirerJobActivity'),
);

const isPageTab = (value: unknown): value is PageTab =>
  value === 'applications' || value === 'notes';

type InitialLoadResponse =
  | { ok: false }
  | {
      ok: true;
      // TODO: simplify this data by re-using the shape similar to AccessAndJob
      jobId: number;
      canBeUpgraded: boolean;
      canPurchaseReferenceChecks: boolean;
      canExportApplicationsToCsv: boolean;
      canShowHomeLocationFilterInCandidateManagement: boolean;
      canShowNationalityFilterInCandidateManagement: boolean;
      isIndirectJobButCanBeChanged: boolean;
      placementV2:
        | {
            __typename?: 'JobPlacementV2ResponseSuccess';
            result: {
              __typename?: 'JobPlacementV2';
              canShowPlacementPrompt: boolean;
              status?: JobPlacementStatus | null;
            };
          }
        | { __typename?: 'ResponseError'; error: ResponseErrorReason };
      highFitCount:
        | {
            __typename?: 'HighFitCountResponseSuccess';
            result: number;
          }
        | {
            __typename?: 'ResponseError';
            error: ResponseErrorReason;
          };
    };

export interface CandidateManagementAppProps {
  employerHeader: JSX.Element;
  pageAnalytics: CandidatesManagementPageAnalytics;
  initialLoadResponse: InitialLoadResponse;
  dataDogRum: PageConfig['dataDogRum'];
  setInitialLoadResponse: React.Dispatch<
    React.SetStateAction<InitialLoadResponse | undefined>
  >;
  jobPlacementExperimentVariant:
    | 'not_yet'
    | 'too_early_to_say'
    | 'still_reviewing_applications'
    | 'control';
  highFitApplicationExperimentVariant: HighFitExperimentVariant;
}

export const CandidateManagementApp: FC<CandidateManagementAppProps> = observer(
  ({
    employerHeader,
    initialLoadResponse,
    pageAnalytics,
    dataDogRum,
    setInitialLoadResponse,
    jobPlacementExperimentVariant,
    highFitApplicationExperimentVariant,
  }) => {
    const { t } = useTranslations(translations);
    const stores = useStores();
    const userStore = stores.userStore;

    if (userStore.userLoadState === UserLoadState.UNAUTHENTICATED) {
      return (
        <PageBlock>
          <PageAlert>
            {t('Sign in to see this page.', {
              Link: (children) => (
                <TextLink
                  key={UserLoadState.UNAUTHENTICATED}
                  href={getSignInUrl()}
                >
                  {children}
                </TextLink>
              ),
            })}
          </PageAlert>
        </PageBlock>
      );
    }

    if (userStore.userLoadState === UserLoadState.FORBIDDEN) {
      return (
        <PageBlock>
          <PageAlert>
            {t(
              "This account can't access this page. Would you like to switch accounts?",
              {
                Link: (children) => (
                  <TextLink key={UserLoadState.FORBIDDEN} href={getSignInUrl()}>
                    {children}
                  </TextLink>
                ),
              },
            )}
          </PageAlert>
        </PageBlock>
      );
    }

    if (
      userStore.userLoadState === UserLoadState.ERROR ||
      !initialLoadResponse.ok
    ) {
      return (
        <PageBlock>
          <Box marginY="small" marginX="gutter">
            <UnhandledErrorPageBlock />
          </Box>
        </PageBlock>
      );
    }

    return (
      <AppPageBlock
        employerHeader={employerHeader}
        jobId={initialLoadResponse.jobId}
        canShowHomeLocationFilterInCandidateManagement={
          initialLoadResponse.canShowHomeLocationFilterInCandidateManagement
        }
        canBeUpgraded={initialLoadResponse.canBeUpgraded}
        canPurchaseReferenceChecks={
          initialLoadResponse.canPurchaseReferenceChecks
        }
        canExportApplicationsToCsv={
          initialLoadResponse.canExportApplicationsToCsv
        }
        canShowNationalityFilterInCandidateManagement={
          initialLoadResponse.canShowNationalityFilterInCandidateManagement
        }
        isIndirectJobButCanBeChanged={
          initialLoadResponse.isIndirectJobButCanBeChanged
        }
        placementV2={initialLoadResponse.placementV2}
        pageAnalytics={pageAnalytics}
        dataDogRum={dataDogRum}
        setInitialLoadResponse={setInitialLoadResponse}
        jobPlacementExperimentVariant={jobPlacementExperimentVariant}
        highFitApplicationExperimentVariant={
          highFitApplicationExperimentVariant
        }
        highFitCount={initialLoadResponse.highFitCount}
      />
    );
  },
);

interface AppPageBlockProps {
  employerHeader: JSX.Element;
  jobId: number;
  canShowHomeLocationFilterInCandidateManagement: boolean;
  canBeUpgraded: boolean;
  canPurchaseReferenceChecks: boolean;
  canExportApplicationsToCsv: boolean;
  canShowNationalityFilterInCandidateManagement: boolean;
  isIndirectJobButCanBeChanged: boolean;
  pageAnalytics: CandidatesManagementPageAnalytics;
  dataDogRum: PageConfig['dataDogRum'];
  placementV2:
    | {
        __typename?: 'JobPlacementV2ResponseSuccess';
        result: {
          __typename?: 'JobPlacementV2';
          canShowPlacementPrompt: boolean;
          status?: JobPlacementStatus | null;
        };
      }
    | { __typename?: 'ResponseError'; error: ResponseErrorReason };
  highFitCount:
    | {
        __typename?: 'HighFitCountResponseSuccess';
        result: number;
      }
    | {
        __typename?: 'ResponseError';
        error: ResponseErrorReason;
      };
  setInitialLoadResponse: React.Dispatch<
    React.SetStateAction<InitialLoadResponse | undefined>
  >;
  jobPlacementExperimentVariant:
    | 'not_yet'
    | 'too_early_to_say'
    | 'still_reviewing_applications'
    | 'control';
  highFitApplicationExperimentVariant: HighFitExperimentVariant;
}

const AppPageBlock: FC<AppPageBlockProps> = observer((props) => {
  const {
    employerHeader,
    jobId,
    canBeUpgraded,
    canExportApplicationsToCsv,
    canPurchaseReferenceChecks,
    canShowHomeLocationFilterInCandidateManagement,
    canShowNationalityFilterInCandidateManagement,
    isIndirectJobButCanBeChanged,
    pageAnalytics,
    dataDogRum,
    placementV2,
    highFitCount,
    jobPlacementExperimentVariant,
    highFitApplicationExperimentVariant,
    setInitialLoadResponse,
  } = props;
  const store = useStores();
  const { seekAdvertiserId, jobApplicationStore, userStore, isMobile } = store;
  const jobStore: IJobStore = store.jobStore;
  const questionStore: IQuestionnaireStore = jobStore.questionnaire;
  const referenceCheckBalance =
    jobStore.job.jobInformation.referenceCheckBalance;

  const [, setPageQueryString] = useQueryParam(
    PAGE_QUERY_PARAM_KEY,
    NumberParam,
  );
  const [selectedPageTabUntyped, setPageTabString] = useQueryParam(
    PAGE_TAB_PARAM_KEY,
    StringParam,
  );
  const [queryParams, setQueryParams] = useQueryParamsStore();
  const [isRelevantApplicationDialogOpen, setIsRelevantApplicationDialogOpen] =
    useState(false);

  const [referenceCheckDialogDetails, setReferenceCheckDialogDetails] =
    useState<{
      prospectId: string;
      candidateFirstName: string;
      candidateFullName: string;
      applicationCorrelationId: string;
      actionOrigin: CheckReferencesActionOrigins;
      actionTarget:
        | 'order-reference-check-dialog'
        | 'purchase-reference-check-dialog'
        | 'unlock-reference-check-dialog';
      currentPage: TrackingCurrentPage;
      candidateId: number;
    } | null>(null);

  const { showDeepLinkedDetailView } = useDeepLinkedDetailView();

  const { language, router } = useAppConfig();
  const [isFilterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const { t } = useTranslations(translations);

  const showToast = useToast();

  const jobSectionRef = useRef<HTMLDivElement | null>(null);

  const { getJobPlacementExpiryTimestamp, hideJobPlacementExpiryTimestamp } =
    useUserActionStatus('job-placement-expiry-timestamp');

  const [isJobPlacementDialogOpen, setIsJobPlacementDialogOpen] = useState<{
    open: boolean;
    isTriggeredFromHeaderLink: boolean;
  }>(() => {
    if (placementV2.__typename !== 'JobPlacementV2ResponseSuccess') {
      return { open: false, isTriggeredFromHeaderLink: false };
    }
    if (!placementV2.result.canShowPlacementPrompt) {
      return { open: false, isTriggeredFromHeaderLink: false };
    }

    const jobListingStartDate = new Date(
      jobStore.job.jobInformation.listingStartedDate,
    );

    if (isNaN(jobListingStartDate.getTime())) {
      return { open: false, isTriggeredFromHeaderLink: false };
    }

    const currentDate = new Date();
    const placementSubmissionCacheExpiryTime = getJobPlacementExpiryTimestamp();
    if (placementSubmissionCacheExpiryTime) {
      const placementSubmissionCacheExpiryDate = new Date(
        placementSubmissionCacheExpiryTime,
      );
      if (
        !isNaN(placementSubmissionCacheExpiryDate.getTime()) &&
        currentDate <= placementSubmissionCacheExpiryDate
      ) {
        return { open: false, isTriggeredFromHeaderLink: false };
      }
    }

    return { open: true, isTriggeredFromHeaderLink: false };
  });

  const { getFilterDrawerState, toggleFilterDrawerState } = useUserActionStatus(
    'filter-drawer-state',
  );

  const questions = jobApplicationStore.filtersStore.questions;

  const { filters, filterFns: useFiltersFns } = useFilters({
    language,
    addCustomError: dataDogRum.addCustomError,
    jobPlacementExperimentVariant,
    highFitApplicationData:
      highFitCount.__typename === 'HighFitCountResponseSuccess' &&
      highFitCount.result > 0
        ? {
            experimentVariant: highFitApplicationExperimentVariant,
            highFitApplicantRatio: `${highFitCount.result}:${undefined}`,
          }
        : {
            experimentVariant: 'ineligible',
          },
  });

  const jobPostingCountry = jobStore.job.jobInformation.jobCountry;

  const applicationFacetsResult = useApplicationsFacetsQuery({
    variables: {
      applicationsFacetsInput: {
        jobId: jobApplicationStore.jobId,
        filters: {
          // We are doing this because the UI allows for searching across all status folders at the moment.
          statusFolders: jobApplicationStore.lastResultSearchText
            ? []
            : ([
                jobApplicationStore.bucket,
              ] as unknown as ApplicationStatusFolder[]),
          searchText: jobApplicationStore.lastResultSearchText,
          homeLocationIds: filters.selectedHomeLocations.map(({ id }) => id),
          questionnaireFilters: jobApplicationStore.filtersStore
            .selectedRrFilters as unknown as QuestionnaireFilter[],
          nationalityRightToWorkIds:
            filters.searchFilters.nationalityRightToWorkIds,
        },
      },
      descriptionInput: {
        displayCountry: jobPostingCountry.toUpperCase(),
        language,
      },
      displayDescriptionInput: {
        language,
      },
      candidateProfileNationalityInput: {
        language,
      },
      canShowNationalityFilter: canShowNationalityFilterInCandidateManagement,
    },
    fetchPolicy: 'no-cache',
  });

  const applicationFacetsDataToUse = applicationFacetsResult.data
    ? applicationFacetsResult.data
    : applicationFacetsResult.previousData;

  const isApplicationFacetsLoading =
    applicationFacetsResult.loading && !applicationFacetsResult.previousData;

  const statusFacets = getStatusFacetsFromQueryData(applicationFacetsDataToUse);

  const getFacetFilterCount = (
    bucketName: BUCKETS,
    statusFacetCounts: ApplicationStatusFacets,
  ) => {
    switch (bucketName) {
      case 'ACCEPT':
        return statusFacetCounts.acceptCount;
      case 'INBOX':
        return statusFacetCounts.inboxCount;
      case 'PRESCREEN':
        return statusFacetCounts.prescreenCount;
      case 'SHORTLIST':
        return statusFacetCounts.shortlistCount;
      case 'INTERVIEW':
        return statusFacetCounts.interviewCount;
      case 'OFFER':
        return statusFacetCounts.offerCount;
      case 'NOT_SUITABLE':
        return statusFacetCounts.rejectedCount;
      default:
        return 0;
    }
  };

  const preQuestionnaireFilterTotal =
    applicationFacetsDataToUse?.applicationsFacets.__typename ===
    'ApplicationsFacetsResponseSuccess'
      ? applicationFacetsDataToUse.applicationsFacets.result
          .preQuestionnaireFilterTotal
      : 0;

  const filtersEventCaptureData: FiltersEventCaptureData = buildFiltersData({
    jobApplicationStore,
    filtersStore: jobApplicationStore.filtersStore,
    homeLocationIds: filters.selectedHomeLocations.map(({ id }) => id) ?? [],
    nationalityIds: filters.searchFilters.nationalityRightToWorkIds ?? [],
    selectedBucketCount: getFacetFilterCount(
      jobApplicationStore.bucket,
      statusFacets,
    ),
    preQuestionnaireFilterTotal,
  });

  const highFitApplicationData: HighFitApplicationData = useMemo(
    () =>
      highFitCount.__typename === 'HighFitCountResponseSuccess' &&
      highFitCount.result > 0
        ? {
            experimentVariant: highFitApplicationExperimentVariant,
            highFitApplicantRatio: `${highFitCount.result}:${
              statusFacets.totalCount ?? undefined
            }`,
          }
        : {
            experimentVariant: 'ineligible',
          },
    [
      highFitApplicationExperimentVariant,
      highFitCount,
      statusFacets.totalCount,
    ],
  );

  const { handleStatusChange } = useHandleStatusChange({
    filters: filters.searchFilters,
    filtersEventCaptureData,
    addCustomError: dataDogRum.addCustomError,
    highFitexperimentVariant: highFitApplicationData.experimentVariant,
  });

  const {
    jobSeekReferenceStatus,
    orderReferenceCheckDialog,
    successReferenceCheckDialog,
    linkAction,
  } = getJobReferenceCheckTrackingProps(referenceCheckBalance);

  const openReferenceCheckDialog = ({
    candidateFirstName,
    candidateFullName,
    applicationCorrelationId,
    currentPage,
    actionOrigin,
    actionTarget,
    candidateId,
    prospectId,
  }: {
    prospectId: string;
    candidateFirstName: string;
    candidateFullName: string;
    applicationCorrelationId: string;
    actionOrigin: CheckReferencesActionOrigins;
    actionTarget:
      | 'order-reference-check-dialog'
      | 'purchase-reference-check-dialog'
      | 'unlock-reference-check-dialog';
    currentPage: TrackingCurrentPage;
    candidateId: number;
  }) => {
    setReferenceCheckDialogDetails({
      candidateFirstName,
      candidateFullName,
      applicationCorrelationId,
      currentPage,
      actionOrigin,
      actionTarget,
      candidateId,
      prospectId,
    });
  };

  useEffect(() => {
    store.jobApplicationStore.updateFromQuery({
      queryParams: {
        jobId: queryParams.jobId,
        positionId: queryParams.positionId,
        bucket: queryParams.bucket,
        page: queryParams.page,
        selectedProspectId: queryParams.selectedProspectId,
        search: queryParams.search,
        overlay: queryParams.overlay,
        referrer: queryParams.referrer,
        tab: queryParams.tab,
      },
      language,
      initialLoad: false,
      filtersEventCaptureData: {
        filtered_apps_in_bucket:
          filtersEventCaptureData.filtered_apps_in_bucket,
        filters_applied_to_bucket:
          filtersEventCaptureData.filters_applied_to_bucket,
        total_apps_in_bucket: filtersEventCaptureData.total_apps_in_bucket,
      },
      filters: filters.searchFilters,
      addCustomError: dataDogRum.addCustomError,
      jobPlacementExperimentVariant,
      highFitApplicationData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryParams.jobId,
    queryParams.positionId,
    queryParams.bucket,
    queryParams.page,
    queryParams.selectedProspectId,
    queryParams.search,
    queryParams.overlay,
    queryParams.referrer,
    queryParams.tab,
    store,
    language,
    filtersEventCaptureData.filtered_apps_in_bucket,
    filtersEventCaptureData.filters_applied_to_bucket,
    filtersEventCaptureData.total_apps_in_bucket,
    filters.searchFilters,
  ]);

  const setSelectedPageTab = (tab: PageTab) => {
    switch (tab) {
      case 'notes':
        setPageTabString('notes');
        break;
      default:
        setPageTabString(undefined);
    }
  };
  const selectedPageTab = isPageTab(selectedPageTabUntyped)
    ? selectedPageTabUntyped
    : 'applications';

  const shouldShowRecommendedCandidates = jobStore.job.isActive;

  const openRelevantApplicationAlertFromBanner = () => {
    trackClickBillableApplicantsDefinition({
      actionOrigin: 'Candidate Listing - Billable Alert',
    });
    setIsRelevantApplicationDialogOpen(true);
  };

  const shouldShowFreemium = userStore.user.canCreateJobs && canBeUpgraded;

  const canShowLocationFilter = canShowHomeLocationFilterInCandidateManagement;

  const hidePageTabs = isMobile && !shouldShowRecommendedCandidates;

  const isMustHaveToggleSelected =
    jobApplicationStore.filtersStore.isMustHaveToggleSelected &&
    !questionStore.autoReject;

  const onSortByChange: (params: { sortBy: string }) => Promise<void> = async ({
    sortBy,
  }) => {
    const defaultPage = 1;

    trackSortOptionChangedEvent({ sortMode: sortBy });
    sendHotjarTag(HOTJAR_TAG_SORT);

    await jobApplicationStore.loadJobApplications(
      {
        sortBy,
        page: defaultPage,
        language,
        filters: filters.searchFilters,
        addCustomError: dataDogRum.addCustomError,
      },
      jobPlacementExperimentVariant,
      highFitApplicationData,
    );
    setPageQueryString(defaultPage, SUPPORT_BACK_BUTTON);
  };

  const renderLocationFilter = ({ mobile }: { mobile: boolean }) => (
    <FilterLocation
      hideLabel={mobile}
      dropdownBoxProps={{
        zIndex: mobile ? undefined : 'dropdown',
        boxShadow: mobile ? undefined : 'medium',
        position: mobile ? 'relative' : 'absolute',
      }}
      locations={
        applicationFacetsDataToUse?.applicationsFacets.__typename ===
        'ApplicationsFacetsResponseSuccess'
          ? applicationFacetsDataToUse.applicationsFacets.result
              .homeLocationFacets
          : []
      }
      loading={isApplicationFacetsLoading}
      jobPostingCountry={jobPostingCountry}
      selectedHomeLocations={filters.selectedHomeLocations}
      setSelectedHomeLocations={({ homeLocations, changes }) => {
        jobApplicationStore.clearAllSelected();
        changes.forEach((change) => {
          pageAnalytics.trackLink({
            event: 'searched_location_pressed',
            attributes: {
              searchLocationId: homeLocations.map(({ id }) => id),
              locationIdSelected: change.item.id,
              filterSelection: change.type,
            },
          });
        });
        useFiltersFns.setSelectedHomeLocations(homeLocations);
        jobApplicationStore.loadJobApplications(
          {
            filters: {
              rrGlobalFilters:
                jobApplicationStore.filtersStore.selectedRrFilters,
              homeLocationIds: homeLocations.map(({ id }) => id),
              nationalityRightToWorkIds:
                filters.searchFilters.nationalityRightToWorkIds,
            },
            language,
            addCustomError: dataDogRum.addCustomError,
          },
          jobPlacementExperimentVariant,
          highFitApplicationData,
        );
      }}
    />
  );

  const renderNationalityFilter = ({ mobile }: { mobile: boolean }) => (
    <FilterNationality
      hideLabel={mobile}
      dropdownBoxProps={{
        zIndex: mobile ? undefined : 'dropdown',
        boxShadow: mobile ? undefined : 'medium',
        position: mobile ? 'relative' : 'absolute',
      }}
      nationalityFacets={
        applicationFacetsDataToUse?.applicationsFacets.__typename ===
        'ApplicationsFacetsResponseSuccess'
          ? (applicationFacetsDataToUse.applicationsFacets.result.nationalityFacets?.map(
              (facet) => ({
                id: facet.nationality.id,
                name: facet.nationality.countryName,
                count: facet.count,
              }),
            ) ?? [])
          : []
      }
      loading={isApplicationFacetsLoading}
      selectedNationalityFacets={filters.selectedNationalities}
      setSelectedNationalityFacets={({
        selectedNationalityFacets,
        changes,
      }) => {
        jobApplicationStore.clearAllSelected();
        changes.forEach((change) => {
          pageAnalytics.trackLink({
            event: 'searched_nationality_pressed',
            attributes: {
              searchNationalityId: selectedNationalityFacets.map(
                ({ id }) => id,
              ),
              nationalityIdSelected: change.item.id,
              filterSelection: change.type,
            },
          });
        });
        useFiltersFns.setSelectedNationalities(selectedNationalityFacets);
        jobApplicationStore.loadJobApplications(
          {
            filters: {
              rrGlobalFilters:
                jobApplicationStore.filtersStore.selectedRrFilters,
              homeLocationIds: filters.searchFilters.homeLocationIds,
              nationalityRightToWorkIds: selectedNationalityFacets.map(
                ({ id }) => id,
              ),
            },
            language,
            addCustomError: dataDogRum.addCustomError,
          },
          jobPlacementExperimentVariant,
          highFitApplicationData,
        );
      }}
    />
  );

  const renderScreeningQuestionFilters = () => {
    const getBaseQuestionnaireAnswerFacets =
      (): FilterScreeningQuestionProps['questionnaireAnswerFacets'] => {
        // In loading, or there are fetch errors:
        if (!applicationFacetsDataToUse) {
          return null;
        }
        // There are payload errors:
        if (
          applicationFacetsDataToUse.applicationsFacets.__typename !==
          'ApplicationsFacetsResponseSuccess'
        ) {
          return null;
        }

        return applicationFacetsDataToUse.applicationsFacets.result.questionnaireAnswerFacets.reduce<
          NonNullable<FilterScreeningQuestionProps['questionnaireAnswerFacets']>
        >((acc, facet) => {
          if (!acc[facet.id]) {
            acc[facet.id] = {
              id: facet.id,
              count: facet.count,
            };
          }
          return acc;
        }, {});
      };

    const questionnaireAnswerFacets = getBaseQuestionnaireAnswerFacets();

    // A SimplifiedFilterAnswer is the aggregation of multiple StandardFilterAnswer
    // For example, in Australia "I have permanent work rights with no restrictions" (AU_Q_6_V_8_H_29013)
    // is the equivalent of "I'm an Australian citizen" (AU_Q_6_V_8_A_14970) and "I'm a permanent resident and/or NZ citizen" (AU_Q_6_V_8_A_14971)
    // Ideally, search cluster should store the SimplifiedFilterAnswer IDs against each application to make it easier to calculate facet
    // However, it's not doing that. So we must calculate facets manually in the UI here 💀
    if (questionnaireAnswerFacets) {
      questions.forEach((question) => {
        question.simplifiedAnswers.forEach((simplifiedAnswers) => {
          const answerIds =
            simplifiedAnswers.answerInfo.answerGlobalIdsOrIds || [];

          const simplifiedAnswerFacetCount = answerIds.reduce(
            (acc, answerId) => {
              const facetCount =
                getQuestionnaireAnswerFacetCount({
                  answerId,
                  questionnaireAnswerFacets,
                }) || 0;

              return acc + facetCount;
            },
            0,
          );

          questionnaireAnswerFacets[simplifiedAnswers.answerInfo.globalIdOrId] =
            {
              id: simplifiedAnswers.answerInfo.globalIdOrId,
              count: simplifiedAnswerFacetCount,
            };
        });
      });
    }

    return (
      <FilterScreeningQuestion
        questionnaire={questionStore}
        isMustHaveToggleSelected={isMustHaveToggleSelected}
        questions={questions}
        onMustHaveFilterToggled={useFiltersFns.onMustHaveFilterToggled}
        onParentFilterSelected={useFiltersFns.onParentFilterSelected}
        onFilterSelected={useFiltersFns.onFilterSelected}
        questionnaireAnswerFacets={questionnaireAnswerFacets}
      />
    );
  };

  const shouldShowClearFilterButton = filters.selectedFilterCount > 0;

  const onClickStatusFolder = (bucket: BUCKETS) => {
    if (jobApplicationStore.bucket !== bucket) {
      jobApplicationStore.clearAllSelected();
    }
    setQueryParams.navigateToBucket(bucket);
  };

  // Adding all filter buttons here
  const renderStatusFolderButtons = () => {
    // Loading state - show skeleton buttons
    if (isApplicationFacetsLoading) {
      return (
        <Stack space="medium">
          {[1, 2, 3, 4, 5, 6, 7].map((key) => (
            <TextSkeleton
              borderRadius="standard"
              width="100%"
              height="large"
              key={key}
            />
          ))}
        </Stack>
      );
    }

    return (
      <StatusFolderButtons
        selectedStatusFolder={
          jobApplicationStore.bucket && jobApplicationStore.isInBucketMode
            ? jobApplicationStore.bucket
            : null
        }
        statusFacets={statusFacets}
        onClickStatusFolder={onClickStatusFolder}
      />
    );
  };

  const filterTabletDesktopContentLayout = (
    <FilterTabletDesktopContentLayout
      locationFilter={
        canShowLocationFilter ? renderLocationFilter({ mobile: false }) : null
      }
      nationalityFilter={
        canShowNationalityFilterInCandidateManagement
          ? renderNationalityFilter({ mobile: false })
          : null
      }
      screeningQuestionFilters={renderScreeningQuestionFilters()}
      statusFolders={renderStatusFolderButtons()}
    />
  );

  const homeLocationIdsForTracking =
    filters.searchFilters.homeLocationIds || [];
  const nationalityIdsForTracking =
    filters.searchFilters.nationalityRightToWorkIds || [];

  const urgentHireModal = (
    <FreemiumUrgentHireModal
      key="freemium-urgent-hire"
      link={router.upgradeJob({
        jobId: jobApplicationStore.jobId,
        ref: 'pop-up-urgent-hiring-candidate-management',
      })}
      jobCreatedDate={jobStore.job.jobInformation.jobCreatedDate}
      trackButtonPressed={() =>
        pageAnalytics.trackLink({
          event: 'candidate_management_freemium_pop_up_upgrade_pressed',
          attributes: {
            seekListingId: jobId,
            alertShownMessageCode:
              'Candidate Management - Freemium Upgrade - Urgent Hiring',
          },
        })
      }
      trackModalDisplayed={() =>
        pageAnalytics.trackView({
          event: 'candidate_management_freemium_pop_up_displayed',
          attributes: {
            seekListingId: jobId,
            alertShownMessageCode:
              'Candidate Management - Freemium Upgrade - Urgent Hiring',
          },
        })
      }
      trackModalDismissed={() =>
        pageAnalytics.trackLink({
          event: 'alert_dismiss_pressed',
          attributes: {
            seekListingId: jobId,
            alertShownMessageCode:
              'Candidate Management - Freemium Upgrade - Urgent Hiring',
          },
        })
      }
    />
  );

  const hardToFillModal = (
    <FreemiumHardToFillModal
      key="freemium-hard-to-fill"
      link={router.upgradeJob({
        jobId: jobApplicationStore.jobId,
        ref: 'pop-up-hard-to-fill-candidate-management',
      })}
      postingLocationCountry={jobPostingCountry}
      jobCreatedDate={jobStore.job.jobInformation.jobCreatedDate}
      numberOfApplications={statusFacets.totalCount}
      trackButtonPressed={() =>
        pageAnalytics.trackLink({
          event: 'candidate_management_freemium_pop_up_upgrade_pressed',
          attributes: {
            seekListingId: jobId,
            alertShownMessageCode:
              'Candidate Management - Freemium Upgrade - Hard to Fill',
          },
        })
      }
      trackModalDisplayed={() =>
        pageAnalytics.trackView({
          event: 'candidate_management_freemium_pop_up_displayed',
          attributes: {
            seekListingId: jobId,
            alertShownMessageCode:
              'Candidate Management - Freemium Upgrade - Hard to Fill',
          },
        })
      }
      trackModalDismissed={() =>
        pageAnalytics.trackLink({
          event: 'alert_dismiss_pressed',
          attributes: {
            seekListingId: jobId,
            alertShownMessageCode:
              'Candidate Management - Freemium Upgrade - Hard to Fill',
          },
        })
      }
    />
  );

  const referenceCreditBalance = getReferenceBalance(referenceCheckBalance);
  const isSeekReferenceJob = Boolean(
    jobStore.job.jobInformation.referenceCheckBalance,
  );

  const { getHighFitErrorType, trackHighFitAlertViewed } = useHighFitError({
    pageAnalytics,
  });
  const highFitErrorType = getHighFitErrorType(
    jobApplicationStore.currentPageJobApplications,
  );

  const renderPageContent = () => {
    if (selectedPageTab === 'applications') {
      return (
        <AppErrorBoundary>
          <Box
            data-cy="main-content"
            paddingY="small"
            style={{ backgroundColor: '#F7F8FB' }} // FIXME: based on Braid, the value should be `neutralSoft` but browser displayed a different value
          >
            {shouldShowFreemium ? [urgentHireModal, hardToFillModal] : null}
            <Box display="flex">
              <Display desktop tablet>
                <FilterDesktop
                  shouldShowClearFilterButton={shouldShowClearFilterButton}
                  content={filterTabletDesktopContentLayout}
                  resetFilters={useFiltersFns.resetFilters}
                  selectedFilterCount={filters.selectedFilterCount}
                  isFilterPanelCollapsed={
                    getFilterDrawerState() === 'collapsed'
                  }
                  toggleFilterPanelCollapsed={toggleFilterDrawerState}
                />
              </Display>
              <Box flexGrow={1}>
                <CandidateList
                  addCustomError={dataDogRum.addCustomError}
                  canPurchaseReferenceChecks={canPurchaseReferenceChecks}
                  actionToolbar={
                    isMobile ? (
                      <SearchResultSummary
                        bucket={jobApplicationStore.bucket}
                        hasSearchText={jobApplicationStore.hasSearchText}
                        matchingApplicationsCount={
                          jobApplicationStore.currentTotalOfJobApplications
                        }
                        selectedFilterCount={filters.selectedFilterCount}
                      />
                    ) : (
                      <ActionToolbar
                        selectedFilterCount={filters.selectedFilterCount}
                        bucket={jobApplicationStore.bucket}
                        currentTotalOfJobApplications={
                          jobApplicationStore.currentTotalOfJobApplications
                        }
                        selectedCount={jobApplicationStore.selectedCount}
                        sortBy={jobApplicationStore.sortBy}
                        hasSearchText={jobApplicationStore.hasSearchText}
                        hasQuestion={Boolean(
                          jobStore.questionnaire?.hasQuestion,
                        )}
                        onSortByChange={onSortByChange}
                        jobTitle={jobStore.job.jobInformation.title}
                        sendProspectEmail={async (params) => {
                          const result =
                            await jobApplicationStore.sendProspectEmail(params);
                          await sleep(1500);
                          await applicationFacetsResult.refetch();
                          return result;
                        }}
                        advertiserName={userStore.user.advertiserName}
                        userEmail={userStore.user.email}
                        userName={userStore.user.name}
                        selectAllStatus={jobApplicationStore.selectAllStatus}
                        matchingApplicationsCount={
                          jobApplicationStore.currentTotalOfJobApplications
                        }
                        selectAll={({
                          isCheckboxChecked,
                          clickSourceForTracking,
                        }) => {
                          if (isCheckboxChecked) {
                            jobApplicationStore.clearAllSelected();
                            if (clickSourceForTracking === 'checkbox') {
                              pageAnalytics.trackLink({
                                event: 'bulk_action_clear_selected_pressed',
                                attributes: {
                                  seekListingId: jobId,
                                  actionOrigin:
                                    'Candidate Listing - Bulk Select Check Box',
                                  bulkActionCount: 0,
                                },
                              });
                            } else if (clickSourceForTracking === 'menuitem') {
                              pageAnalytics.trackLink({
                                event: 'candidate_list_action_pressed',
                                attributes: {
                                  linkAction: 'select all on page',
                                  seekListingId: jobId,
                                  bulkActionCount: 0,
                                },
                              });
                            }
                          } else {
                            jobApplicationStore.clickSelectAllOnCurrentPage();
                            if (clickSourceForTracking === 'checkbox') {
                              pageAnalytics.trackLink({
                                event: 'bulk_actions_select_all_pressed',
                                attributes: {
                                  seekListingId: jobId,
                                  objectInteraction: 'select-all',
                                  bulkActionCount:
                                    jobApplicationStore
                                      .currentPageJobApplications.length,
                                },
                              });
                            } else if (clickSourceForTracking === 'menuitem') {
                              pageAnalytics.trackLink({
                                event: 'candidate_list_action_pressed',
                                attributes: {
                                  seekListingId: jobId,
                                  linkAction: 'select all on page',
                                  bulkActionCount:
                                    jobApplicationStore
                                      .currentPageJobApplications.length,
                                },
                              });
                            }
                          }
                        }}
                        deselectAll={() => {
                          jobApplicationStore.clearAllSelected();
                          pageAnalytics.trackLink({
                            event: 'candidate_list_action_pressed',
                            attributes: {
                              seekListingId: jobId,
                              linkAction: 'deselect all',
                              bulkActionCount: 0,
                            },
                          });
                        }}
                        onExpandApplicationCardState={() => {
                          trackCandidateCardStatus({
                            candidateCardStatus: 'Expand',
                            actionOrigin: 'Candidate Listing - Listing Change',
                          });
                        }}
                        onCollapseApplicationCardState={() => {
                          trackCandidateCardStatus({
                            candidateCardStatus: 'Collapse',
                            actionOrigin: 'Candidate Listing - Listing Change',
                          });
                        }}
                        selectAllJobApplicationsInBucket={({
                          shouldShowLoading,
                          onCompleted,
                        }) => {
                          jobApplicationStore.loadAllJobApplications({
                            addCustomError: dataDogRum.addCustomError,
                            selectAll: true,
                            shouldShowLoading,
                            onCompleted: () => {
                              pageAnalytics.trackLink({
                                event: 'candidate_list_action_pressed',
                                attributes: {
                                  seekListingId: jobId,
                                  linkAction: 'select all in folder',
                                  bulkActionCount:
                                    jobApplicationStore.selectedCount,
                                },
                              });
                              onCompleted();
                            },
                            filters: filters.searchFilters,
                          });
                        }}
                        onOpenBulkEmailDialog={() => {
                          trackBulkActionsEmailTabClicked(
                            jobApplicationStore.bulkActionEventAttributes,
                          );
                          sendHotjarTag(HOTJAR_TAG_BULK_ACTIONS);
                        }}
                        onOpenBulkMoveDialog={() => {
                          trackBulkActionsMoveTabClicked(
                            jobApplicationStore.bulkActionEventAttributes,
                          );
                          sendHotjarTag(HOTJAR_TAG_BULK_ACTIONS);

                          if (
                            jobApplicationStore.selectedJobApplicationIds
                              .length > BULK_MOVE_LIMIT
                          ) {
                            jobApplicationStore.selectedJobApplications
                              .slice(
                                BULK_MOVE_LIMIT,
                                jobApplicationStore.selectedJobApplications
                                  .length,
                              )
                              .forEach((jobApplication) =>
                                jobApplication.updateSelected(false),
                              );
                            showToast({
                              message: t(
                                'You can move up to bulkMoveLimit applications at a time',
                                { bulkMoveLimit: BULK_MOVE_LIMIT },
                              ),
                              description: t(
                                'We have selected the first bulkMoveLimit in this folder.',
                                { bulkMoveLimit: BULK_MOVE_LIMIT },
                              ),
                              tone: 'critical',
                            });
                            return false;
                          }
                          return true;
                        }}
                        onClickSelectionMenu={() => {
                          pageAnalytics.trackLink({
                            event: 'candidate_list_action_menu_pressed',
                            attributes: {
                              seekListingId: jobId,
                            },
                          });
                        }}
                        updateStatusForSelectedApplications={(newBucket) => {
                          trackUpdateStatusMultiple({
                            objectInteraction:
                              jobApplicationStore.selectAllStatus,
                            bulkActionCount:
                              jobApplicationStore
                                .selectedJobApplicationUpToBulkMoveLimit.length,
                            applicationStatus: getDashisedBucket(newBucket), // new bucket/status
                            bucket: getDashisedBucket(
                              jobApplicationStore.bucket,
                            ), // old bucket/status
                            candidateCardStatus:
                              jobApplicationStore.rootStore.getCandidateCardStateForTracking(),
                            searchLocationId: homeLocationIdsForTracking,
                            searchNationalityId: nationalityIdsForTracking,
                          });

                          return jobApplicationStore.updateStatusForSelectedApplications(
                            {
                              bucket: newBucket,
                              store: jobApplicationStore.rootStore,
                              reloadJobApplications: async () => {
                                await jobApplicationStore.loadJobApplications(
                                  {
                                    language,
                                    filters: filters.searchFilters,
                                    addCustomError: dataDogRum.addCustomError,
                                  },
                                  jobPlacementExperimentVariant,
                                  highFitApplicationData,
                                );
                              },
                              filtersEventCaptureData,
                              addCustomError: dataDogRum.addCustomError,
                            },
                          );
                        }}
                        // We are using a function here because accessing the mobx array directly causes rerenders. See PR #3167
                        getSelectedJobApplicationNames={() =>
                          jobApplicationStore.selectedJobApplications.map(
                            ({ fullName }) => fullName,
                          )
                        }
                        getSelectedJobApplicationsNameToBulkMoveLimit={() =>
                          jobApplicationStore.selectedJobApplicationUpToBulkMoveLimit.map(
                            ({ fullName }) => fullName,
                          )
                        }
                        onClickViewBulkMoveJobApps={(newStatus) => {
                          jobApplicationStore.clickViewMovedJobApplications();
                          setQueryParams.navigateToBucket(newStatus);
                        }}
                        addCustomError={dataDogRum.addCustomError}
                        refetchFacets={async () => {
                          await sleep(1500);
                          await applicationFacetsResult.refetch();
                        }}
                      />
                    )
                  }
                  reloadJobApplicationsWithLastResultSearchText={() => {
                    jobApplicationStore.loadJobApplications(
                      {
                        searchText: jobApplicationStore.lastResultSearchText,
                        language,
                        filters: filters.searchFilters,
                        addCustomError: dataDogRum.addCustomError,
                      },
                      jobPlacementExperimentVariant,
                      highFitApplicationData,
                    );
                  }}
                  clearTextAndReloadJobApplications={() => {
                    jobApplicationStore.updateSearchText('');
                    // FIXME: This is not updating the query param for search term, which is a bug
                    jobApplicationStore.loadJobApplications(
                      {
                        language,
                        searchText: jobApplicationStore.searchText,
                        filters: filters.searchFilters,
                        addCustomError: dataDogRum.addCustomError,
                      },
                      jobPlacementExperimentVariant,
                      highFitApplicationData,
                    );
                  }}
                  openRelevantApplicationDialog={
                    openRelevantApplicationAlertFromBanner
                  }
                  useFiltersFns={useFiltersFns}
                  isMustHaveToggleSelected={isMustHaveToggleSelected}
                  filtersEventCaptureData={filtersEventCaptureData}
                  handleStatusChange={handleStatusChange}
                  onDetailViewMounted={() => {
                    sendDetailView({
                      jobApplication:
                        jobApplicationStore.shownDetailsJobApplication,
                      store,
                      addCustomError: dataDogRum.addCustomError,
                    });
                  }}
                  refetchFacets={async () => {
                    // NOTE: Status update is async so we need to wait a bit after the mutation has returned to refetch or else we'd get the same results.
                    // This also solves the issue where
                    // 1. AppPageBlock is updated by MobX's `observer` function
                    // 2. Route is updated by changing of search params (inside onUpdateSuccess)
                    //
                    // (1.) runs first, and when it does, it still has the old query params with `&selected=<id>` which causes
                    // the profile drawer to re-open when `store.jobApplicationStore.updateFromQuery` is called in AppPageBlock
                    // Because of this, the drawer re-opens after it has been closed
                    // Reported: https://seekchat.slack.com/archives/C24PK4XV3/p1719356837551939
                    await sleep(1500);
                    await applicationFacetsResult.refetch();
                  }}
                  homeLocationIdsForTracking={homeLocationIdsForTracking}
                  nationalityIdsForTracking={nationalityIdsForTracking}
                  onVerificationsIconClick={({ candidateId }) => {
                    pageAnalytics.trackLink({
                      event: 'verifications_in_candidate_list_pressed',
                      attributes: {
                        seekListingId: jobId,
                        candidateId,
                      },
                    });
                  }}
                  onCheckReferencesButtonClick={(prop) => {
                    openReferenceCheckDialog(prop);
                    pageAnalytics.trackLink({
                      event: 'reference_check_pressed',
                      attributes: {
                        isSeekReferenceJob,
                        seekReferenceBalance: referenceCreditBalance,
                        applicationCorrelationId: prop.applicationCorrelationId,
                        bucket: mapBucketsToAnalyticsApplicationStatus(
                          jobApplicationStore.bucket,
                        ),
                        candidateId: prop.candidateId,
                        prospectId: prop.prospectId,
                        currentPage: prop.currentPage,
                        jobSeekReferenceStatus,
                        seekListingId: jobId,
                      },
                    });
                    sendHotjarTag(HOTJAR_ORDER_REFERENCE_CHECK_BUTTON_CLICKED);
                  }}
                  onReferencesIconButtonClick={({
                    applicationCorrelationId,
                    candidateId,
                    prospectId,
                    isSeekReferenceApplication,
                    applicationSeekReferenceStatus,
                    referencesReceived,
                  }) => {
                    pageAnalytics.trackLink({
                      event: 'references_in_candidate_list_pressed',
                      attributes: {
                        applicationCorrelationId,
                        bucket: mapBucketsToAnalyticsApplicationStatus(
                          jobApplicationStore.bucket,
                        ),
                        candidateId,
                        isSeekReferenceApplication,
                        referencesReceived,
                        prospectId,
                        applicationSeekReferenceStatus,
                        seekListingId: jobId,
                      },
                    });
                    sendHotjarTag(HOTJAR_VIEW_REFERENCE_CHECK_BUTTON_CLICKED);
                  }}
                  onUpgradeReferenceButtonClick={({ dialogName, ...prop }) => {
                    openReferenceCheckDialog(prop);
                    pageAnalytics.trackLink({
                      event: 'upgrade_reference_pressed',
                      attributes: {
                        isSeekReferenceJob,
                        seekReferenceBalance: referenceCreditBalance,
                        applicationCorrelationId: prop.applicationCorrelationId,
                        bucket: mapBucketsToAnalyticsApplicationStatus(
                          jobApplicationStore.bucket,
                        ),
                        candidateId: prop.candidateId,
                        prospectId: prop.prospectId,
                        currentPage: prop.currentPage,
                        jobSeekReferenceStatus,
                        seekListingId: jobId,
                        dialogName,
                      },
                    });
                    sendHotjarTag(HOTJAR_UPGRADE_REFERENCES_BUTTON_CLICKED);
                  }}
                  hasNoCandidates={statusFacets.totalCount === 0}
                  preQuestionnaireFilterTotal={preQuestionnaireFilterTotal}
                  referenceChecksBalance={referenceCheckBalance}
                  highFitError={
                    highFitErrorType &&
                    highFitApplicationData.experimentVariant ===
                      'challenger' ? (
                      <TrackOnMount
                        onMount={() =>
                          trackHighFitAlertViewed(highFitErrorType)
                        }
                      >
                        <HighFitErrorMessage errorType={highFitErrorType} />
                      </TrackOnMount>
                    ) : null
                  }
                  highFitExperimentVariant={
                    highFitApplicationData.experimentVariant
                  }
                  autoRejectBanner={
                    jobApplicationStore.bucket === BUCKETS.NOT_SUITABLE &&
                    Boolean(statusFacets.rejectedCount) &&
                    questionStore.autoReject ? (
                      <AutoRejectBanner />
                    ) : null
                  }
                />
              </Box>
            </Box>
          </Box>
        </AppErrorBoundary>
      );
    }
    if (selectedPageTab === 'notes') {
      if (!userStore.user.userId) {
        return <></>;
      }
      return (
        <HirerJobActivity
          handleStatusChange={handleStatusChange}
          addCustomError={dataDogRum.addCustomError}
          userId={String(userStore.user.userId)}
          jobId={String(jobId)}
          seekAdvertiserId={seekAdvertiserId}
          referenceChecksBalance={referenceCheckBalance}
          canPurchaseReferenceChecks={canPurchaseReferenceChecks}
          onDetailViewMounted={() => {
            sendDetailView({
              jobApplication: jobApplicationStore.shownDetailsJobApplication,
              store,
              addCustomError: dataDogRum.addCustomError,
            });
          }}
        />
      );
    }
  };

  const pageTabs = (
    <PageTabs
      selectedPageTab={selectedPageTab}
      setSelectedPageTab={setSelectedPageTab}
      showRecommendedCandidates={shouldShowRecommendedCandidates}
      jobId={jobApplicationStore.jobId}
      onApplicationsTabClick={() =>
        jobApplicationStore.loadJobApplications(
          {
            language,
            filters: filters.searchFilters,
            addCustomError: dataDogRum.addCustomError,
          },
          jobPlacementExperimentVariant,
          highFitApplicationData,
        )
      }
    />
  );

  const mobileFilterSection = (
    <MobileFilterSection
      sortBy={jobApplicationStore.sortBy}
      hasSearchText={jobApplicationStore.hasSearchText}
      hasQuestion={Boolean(jobStore.questionnaire?.hasQuestion)}
      onSortByChange={onSortByChange}
      selectedFilterCount={filters.selectedFilterCount}
      openFilterDrawer={() => setFilterDrawerOpen(true)}
    />
  );

  return (
    <>
      <IntercomConfiguration />
      <AppPageLayout
        employerHeader={employerHeader}
        jobSectionRef={jobSectionRef}
        mobileStickyHeader={
          isMobile && (
            <MobileStickyHeader
              employerHeader={employerHeader}
              pageTabs={shouldShowRecommendedCandidates && pageTabs}
              mobileFilterSection={mobileFilterSection}
            />
          )
        }
        jobSection={{
          boxShadow: hidePageTabs ? 'small' : undefined,
          element: (
            <JobSection
              jobId={jobId}
              job={jobStore.job}
              addCustomError={dataDogRum.addCustomError}
              isRecommendedCandidatesTabShown={shouldShowRecommendedCandidates}
              heading={
                <JobTitleHeadingContent
                  jobStatus={{
                    isExpired: jobStore.job.isExpired,
                    isPending: jobStore.job.isPending,
                  }}
                  jobTitle={jobStore.job.jobInformation.title}
                />
              }
              details={
                <JobStatDetailLayout
                  placementStatusLink={
                    <PlacementStatusLink
                      openJobPlacementDialog={() =>
                        setIsJobPlacementDialogOpen({
                          open: true,
                          isTriggeredFromHeaderLink: true,
                        })
                      }
                      placementV2={placementV2}
                      trackJobPlacementStatusLinkClickedEvent={(
                        hiringStatus,
                      ) => {
                        pageAnalytics.trackLink({
                          event: 'hiring_in_progress_pressed',
                          attributes: {
                            seekListingId: jobId,
                            hiringStatus,
                          },
                        });
                      }}
                    />
                  }
                  expireDate={
                    <JobExpireDate
                      jobStatus={jobStore.job.jobInformation.status}
                      numberOfDaysRemaining={
                        jobStore.job.jobInformation.numberOfDaysRemaining
                      }
                    />
                  }
                  totalApplications={
                    statusFacets.totalCount !== null ? (
                      <TotalApplications
                        totalApplications={statusFacets.totalCount}
                      />
                    ) : (
                      <></>
                    )
                  }
                  billableApplicants={
                    <BillableApplicantsLink
                      billableApplicants={
                        jobStore.job.jobInformation.billableApplicants
                      }
                      openRelevantApplicationDialog={() => {
                        trackClickBillableApplicantsDefinition({
                          actionOrigin:
                            'Candidate Listing - Billable Applicants Button',
                        });
                        setIsRelevantApplicationDialogOpen(true);
                      }}
                    />
                  }
                  referenceCheckBalance={
                    !referenceCheckBalance &&
                    !canPurchaseReferenceChecks ? null : (
                      <ReferenceCheckBalanceLink
                        canPurchaseReferenceChecks={canPurchaseReferenceChecks}
                        jobId={jobId}
                        onClick={() => {
                          pageAnalytics.trackLink({
                            event: 'references_balance_pressed',
                            attributes: {
                              seekListingId: jobId,
                              jobSeekReferenceStatus,
                            },
                          });
                          sendHotjarTag(HOTJAR_BUDGETS_LINK_BUTTON_CLICKED);
                        }}
                        referenceBalance={referenceCheckBalance}
                        trackReferenceCheckBalanceDialogDisplayedEvent={() => {
                          pageAnalytics.trackView({
                            event: 'dialog_displayed',
                            attributes: {
                              seekListingId: jobId,
                              dialogName:
                                'Candidate Management - How Reference Checks Work',
                            },
                          });
                        }}
                        trackReferenceCheckBalanceDialogDismissedEvent={() => {
                          pageAnalytics.trackLink({
                            event: 'dialog_dismiss_pressed',
                            attributes: {
                              seekListingId: jobId,
                              dialogName:
                                'Candidate Management - How Reference Checks Work',
                            },
                          });
                        }}
                        trackPurchaseAddOnDisplayedEvent={() => {
                          pageAnalytics.trackView({
                            event: 'dialog_displayed',
                            attributes: {
                              dialogName:
                                'Candidate Management - Unlock Full Reference Check Upgrade Now',
                              actionOrigin: 'header reference balance pressed',
                              applicationCorrelationId: null,
                              bucket: mapBucketsToAnalyticsApplicationStatus(
                                jobApplicationStore.bucket, // TODO: double check if hirer current status view or application status
                              ),
                              candidateId: null,
                              currentPage: 'candidate list',
                              isSeekReferenceJob,
                              jobSeekReferenceStatus,
                              prospectId: null,
                              seekListingId: jobId,
                              seekReferenceBalance: referenceCreditBalance,
                            },
                          });
                        }}
                        trackPurchaseAddOnClickEvent={() => {
                          pageAnalytics.trackLink({
                            event: 'purchase_reference_check_add_on_pressed',
                            attributes: {
                              actionOrigin: 'header reference balance pressed',
                              isSeekReferenceJob,
                              jobSeekReferenceStatus,
                              seekReferenceBalance: referenceCreditBalance,
                              bucket: mapBucketsToAnalyticsApplicationStatus(
                                jobApplicationStore.bucket,
                              ),
                              currentPage: 'candidate list',
                              seekListingId: jobId,
                              candidateId: null,
                              prospectId: null,
                              applicationCorrelationId: null,
                              linkAction:
                                'Go To Purchase Reference Check Add On Modal',
                              linkText: 'purchase add on',
                              dialogName:
                                'Candidate Management - Unlock Full Reference Check Upgrade Now',
                            },
                          });
                        }}
                      />
                    )
                  }
                />
              }
              alert={
                jobStore.job.isPending ? (
                  <Box paddingY="medium">
                    <PendingAlert />
                  </Box>
                ) : null
              }
              freemiumButton={
                shouldShowFreemium ? (
                  <FreemiumButton
                    link={router.upgradeJob({
                      jobId: jobApplicationStore.jobId,
                      ref: 'candidate-listing-candidate-management',
                    })}
                    onFreemiumButtonClicked={() =>
                      trackFreemiumButtonPressedEvent()
                    }
                    onFreemiumButtonLoaded={() => {
                      trackFreemiumButtonDisplay({
                        hasJobUpgradeButton: true,
                      });
                    }}
                  />
                ) : null
              }
              renderJobActionMenu={({ onAction }) =>
                !jobStore.isLoading ? (
                  <JobActionMenu
                    job={jobStore.job}
                    canExportApplicationsToCsv={canExportApplicationsToCsv}
                    isIndirectJobButCanBeChanged={isIndirectJobButCanBeChanged}
                    onAction={onAction}
                  />
                ) : null
              }
              seekAdvertiserId={jobStore.job.seekAdvertiserId}
            />
          ),
        }}
        pageTabs={!hidePageTabs && pageTabs}
        mobileFilterSection={mobileFilterSection}
        content={renderPageContent()}
      />
      {jobStore.job.jobInformation.billableApplicants && (
        <RelevantApplicationDialog
          isOpen={isRelevantApplicationDialogOpen}
          mandatoryQuestions={questionStore.getMandatoryQuestions()}
          onClose={() => setIsRelevantApplicationDialogOpen(false)}
          relevantApplicationStats={
            jobStore.job.jobInformation.billableApplicants
          }
        />
      )}

      {referenceCheckDialogDetails?.actionTarget ===
        'order-reference-check-dialog' &&
      referenceCreditBalance !== null &&
      referenceCheckBalance ? (
        <OrderReferenceCheckDialog
          open={true}
          onClose={() => {
            setReferenceCheckDialogDetails(null);
          }}
          candidateDetails={referenceCheckDialogDetails}
          jobId={jobId}
          onCompleted={({ applicationCorrelationId, referenceCheckStatus }) => {
            if (jobStore.job.jobInformation.referenceCheckBalance) {
              jobStore.job.jobInformation.deductReferenceCheckBalance();
              jobApplicationStore.jobApplications
                .find(
                  (jobApplication) =>
                    applicationCorrelationId ===
                    jobApplication.applicationCorrelationId,
                )
                ?.setReferenceCheckStatus(referenceCheckStatus);
            }
          }}
          viewReport={() => {
            showDeepLinkedDetailView({
              jobApplicationId: referenceCheckDialogDetails.prospectId,
              tab: isMobile
                ? JOB_APPLICATION_TAB.PROFILE
                : JOB_APPLICATION_TAB.REFERENCES,
            });
            pageAnalytics.trackLink({
              event: 'content_element_pressed',
              attributes: {
                elementAction: 'Redirection to reference report',
                elementDesign: 'notice',
                elementText: 'View the report',
              },
            });
          }}
          referenceCreditBalance={referenceCreditBalance}
          budgetType={referenceCheckBalance.budgetType}
          trackOrderReferenceCheckDialogDisplayedEvent={({
            currentPage,
            candidateId,
            prospectId,
            applicationCorrelationId,
          }) => {
            pageAnalytics.trackView({
              event: 'dialog_displayed',
              attributes: {
                isSeekReferenceJob,
                seekReferenceBalance: referenceCreditBalance,
                bucket: mapBucketsToAnalyticsApplicationStatus(
                  jobApplicationStore.bucket,
                ),
                currentPage,
                seekListingId: jobId,
                candidateId,
                prospectId,
                applicationCorrelationId,
                dialogName: orderReferenceCheckDialog,
                jobSeekReferenceStatus,
              },
            });
          }}
          trackOrderReferenceCheckDialogDismissedEvent={({
            currentPage,
            candidateId,
            prospectId,
            applicationCorrelationId,
          }) => {
            pageAnalytics.trackLink({
              event: 'dialog_dismiss_pressed',
              attributes: {
                isSeekReferenceJob,
                seekReferenceBalance: referenceCreditBalance,
                bucket: mapBucketsToAnalyticsApplicationStatus(
                  jobApplicationStore.bucket,
                ),
                currentPage,
                seekListingId: jobId,
                candidateId,
                prospectId,
                applicationCorrelationId,
                dialogName: orderReferenceCheckDialog,
                jobSeekReferenceStatus,
              },
            });
          }}
          trackOrderReferenceCheckPressedEvent={({
            currentPage,
            candidateId,
            prospectId,
            applicationCorrelationId,
          }) => {
            pageAnalytics.trackLink({
              event: 'order_reference_check_pressed',
              attributes: {
                isSeekReferenceJob,
                seekReferenceBalance: referenceCreditBalance,
                bucket: mapBucketsToAnalyticsApplicationStatus(
                  jobApplicationStore.bucket,
                ),
                currentPage,
                seekListingId: jobId,
                candidateId,
                prospectId,
                applicationCorrelationId,
                jobSeekReferenceStatus,
                linkAction,
                linkText: 'Order reference check',
                dialogName: orderReferenceCheckDialog,
              },
            });
          }}
          trackOrderReferenceCheckErrorDialogDisplayedEvent={(currentPage) => {
            pageAnalytics.trackView({
              event: 'dialog_displayed',
              attributes: {
                seekListingId: jobId,
                dialogName:
                  "Candidate Management - SEEKRef - Couldn't check references",
                currentPage,
              },
            });
          }}
          trackOrderReferenceCheckErrorDialogDismissedEvent={(currentPage) => {
            pageAnalytics.trackLink({
              event: 'dialog_dismiss_pressed',
              attributes: {
                seekListingId: jobId,
                dialogName:
                  "Candidate Management - SEEKRef - Couldn't check references",
                currentPage,
              },
            });
          }}
          trackOrderReferenceCheckSuccessDialogDisplayedEvent={({
            currentPage,
            candidateId,
            prospectId,
            applicationCorrelationId,
          }) => {
            pageAnalytics.trackView({
              event: 'dialog_displayed',
              attributes: {
                isSeekReferenceJob,
                seekReferenceBalance: referenceCreditBalance,
                bucket: mapBucketsToAnalyticsApplicationStatus(
                  jobApplicationStore.bucket,
                ),
                currentPage,
                seekListingId: jobId,
                candidateId,
                prospectId,
                applicationCorrelationId,
                dialogName: successReferenceCheckDialog,
                jobSeekReferenceStatus,
              },
            });
          }}
          trackOrderReferenceCheckSuccessDialogDismissedEvent={({
            currentPage,
            candidateId,
            prospectId,
            applicationCorrelationId,
          }) => {
            pageAnalytics.trackLink({
              event: 'dialog_dismiss_pressed',
              attributes: {
                isSeekReferenceJob,
                seekReferenceBalance: referenceCreditBalance,
                bucket: mapBucketsToAnalyticsApplicationStatus(
                  jobApplicationStore.bucket,
                ),
                currentPage,
                seekListingId: jobId,
                candidateId,
                prospectId,
                applicationCorrelationId,
                dialogName: successReferenceCheckDialog,
                jobSeekReferenceStatus,
              },
            });
          }}
          trackReuseToastDisplayEvent={() => {
            pageAnalytics.trackView({
              event: 'content_element_viewed',
              attributes: {
                elementDesign: 'notice',
                elementText: 'Reference available',
              },
            });
          }}
        />
      ) : null}

      {referenceCheckDialogDetails?.actionTarget ===
        'purchase-reference-check-dialog' && canPurchaseReferenceChecks ? (
        <PurchaseReferenceCheckAddOnDialog
          actionOrigin={referenceCheckDialogDetails.actionOrigin}
          jobId={jobId}
          onClickPurchase={() => {
            pageAnalytics.trackLink({
              event: 'purchase_reference_check_add_on_pressed',
              attributes: {
                actionOrigin: referenceCheckDialogDetails.actionOrigin,
                isSeekReferenceJob,
                jobSeekReferenceStatus,
                seekReferenceBalance: referenceCreditBalance,
                bucket: mapBucketsToAnalyticsApplicationStatus(
                  jobApplicationStore.bucket,
                ),
                currentPage: referenceCheckDialogDetails.currentPage,
                seekListingId: jobId,
                candidateId: referenceCheckDialogDetails.candidateId,
                prospectId: referenceCheckDialogDetails.prospectId,
                applicationCorrelationId:
                  referenceCheckDialogDetails.applicationCorrelationId,
                linkAction: 'Go To Purchase Reference Check Add On Modal',
                linkText: 'purchase add on',
                dialogName:
                  'Candidate Management - Unlock Full Reference Check Upgrade Now',
              },
            });
          }}
          onClose={() => {
            pageAnalytics.trackLink({
              event: 'dialog_dismiss_pressed',
              attributes: {
                isSeekReferenceJob,
                seekReferenceBalance: referenceCreditBalance,
                bucket: mapBucketsToAnalyticsApplicationStatus(
                  jobApplicationStore.bucket,
                ),
                currentPage: referenceCheckDialogDetails.currentPage,
                seekListingId: jobId,
                candidateId: referenceCheckDialogDetails.candidateId,
                prospectId: referenceCheckDialogDetails.prospectId,
                applicationCorrelationId:
                  referenceCheckDialogDetails.applicationCorrelationId,
                dialogName:
                  'Candidate Management - Unlock Full Reference Check Upgrade Now',
                jobSeekReferenceStatus,
              },
            });
            setReferenceCheckDialogDetails(null);
          }}
          onMount={() =>
            pageAnalytics.trackView({
              event: 'dialog_displayed',
              attributes: {
                dialogName:
                  'Candidate Management - Unlock Full Reference Check Upgrade Now',
                actionOrigin: referenceCheckDialogDetails.actionOrigin,
                applicationCorrelationId:
                  referenceCheckDialogDetails.applicationCorrelationId,
                bucket: mapBucketsToAnalyticsApplicationStatus(
                  jobApplicationStore.bucket, // TODO: double check if hirer current status view or application status
                ),
                candidateId: referenceCheckDialogDetails.candidateId,
                currentPage: referenceCheckDialogDetails.currentPage,
                isSeekReferenceJob,
                jobSeekReferenceStatus,
                prospectId: referenceCheckDialogDetails.prospectId,
                seekListingId: jobId,
                seekReferenceBalance: referenceCreditBalance,
              },
            })
          }
          open={true}
        />
      ) : null}

      <PurchaseReferenceCheckErrorDialog
        open={
          referenceCheckDialogDetails?.actionTarget ===
            'purchase-reference-check-dialog' && !canPurchaseReferenceChecks
        }
        onClose={() => setReferenceCheckDialogDetails(null)}
      />

      {referenceCheckDialogDetails?.actionTarget ===
        'unlock-reference-check-dialog' &&
      referenceCheckBalance &&
      !referenceCheckBalance.expired ? (
        <UnlockReferenceCheckDialog
          open={
            referenceCheckDialogDetails.actionTarget ===
            'unlock-reference-check-dialog'
          }
          onClose={() => {
            pageAnalytics.trackLink({
              event: 'dialog_dismiss_pressed',
              attributes: {
                isSeekReferenceJob,
                seekReferenceBalance: referenceCreditBalance,
                bucket: mapBucketsToAnalyticsApplicationStatus(
                  jobApplicationStore.bucket,
                ),
                currentPage: referenceCheckDialogDetails.currentPage,
                seekListingId: jobId,
                candidateId: referenceCheckDialogDetails.candidateId,
                prospectId: referenceCheckDialogDetails.prospectId,
                applicationCorrelationId:
                  referenceCheckDialogDetails.applicationCorrelationId,
                dialogName: 'Candidate Management - Unlock Reference Report',
                jobSeekReferenceStatus,
              },
            });
            setReferenceCheckDialogDetails(null);
          }}
          jobId={jobId.toString()}
          viewReport={() => {
            showDeepLinkedDetailView({
              jobApplicationId: referenceCheckDialogDetails.prospectId,
              tab: isMobile
                ? JOB_APPLICATION_TAB.PROFILE
                : JOB_APPLICATION_TAB.REFERENCES,
            });
          }}
          candidateDetails={{
            candidateFirstName: referenceCheckDialogDetails.candidateFirstName,
            applicationCorrelationId:
              referenceCheckDialogDetails.applicationCorrelationId,
          }}
          onCompleted={() => {
            if (jobStore.job.jobInformation.referenceCheckBalance) {
              jobStore.job.jobInformation.deductReferenceCheckBalance();
              jobApplicationStore.jobApplications
                .find(
                  (jobApplication) =>
                    referenceCheckDialogDetails.applicationCorrelationId ===
                    jobApplication.applicationCorrelationId,
                )
                .unlockReferenceCheck();
            }
          }}
          remainingCount={referenceCheckBalance.availableBalance - 1}
          onClickUpgradeReferenceButton={() => {
            pageAnalytics.trackLink({
              event: 'order_reference_check_pressed',
              attributes: {
                isSeekReferenceJob: true,
                jobSeekReferenceStatus,
                seekReferenceBalance: referenceCheckBalance.availableBalance,
                bucket: mapBucketsToAnalyticsApplicationStatus(
                  jobApplicationStore.bucket,
                ),
                applicationCorrelationId:
                  referenceCheckDialogDetails.applicationCorrelationId,
                prospectId: referenceCheckDialogDetails.prospectId,
                candidateId: referenceCheckDialogDetails.candidateId,
                currentPage: referenceCheckDialogDetails.currentPage,
                dialogName: 'Candidate Management - Unlock Reference Report',
                linkAction: 'Unlock Reference Check',
                linkText: 'unlock reference',
                seekListingId: jobId,
              },
            });
          }}
          onMount={() => {
            pageAnalytics.trackView({
              event: 'dialog_displayed',
              attributes: {
                isSeekReferenceJob,
                seekReferenceBalance: referenceCreditBalance,
                bucket: mapBucketsToAnalyticsApplicationStatus(
                  jobApplicationStore.bucket,
                ),
                currentPage: referenceCheckDialogDetails.currentPage,
                seekListingId: jobId,
                prospectId: referenceCheckDialogDetails.prospectId,
                candidateId: referenceCheckDialogDetails.candidateId,
                applicationCorrelationId:
                  referenceCheckDialogDetails.applicationCorrelationId,
                dialogName: 'Candidate Management - Unlock Reference Report',
                jobSeekReferenceStatus,
              },
            });
          }}
        />
      ) : null}

      {isJobPlacementDialogOpen.open && (
        <JobPlacementDialog
          jobSectionRef={jobSectionRef}
          jobId={jobId}
          openDialog={isJobPlacementDialogOpen}
          onClose={() =>
            setIsJobPlacementDialogOpen((prev) => ({ ...prev, open: false }))
          }
          hideJobPlacementInLocalStorage={() =>
            hideJobPlacementExpiryTimestamp()
          }
          shouldShowUpgradeAdDialog={shouldShowFreemium}
          shouldShowAdPerformanceDialog={
            jobStore.job.jobInformation.hasPerformanceStats
          }
          placementV2={placementV2}
          onPlacementStatusChange={(status) => {
            setInitialLoadResponse((prev) => {
              if (
                !prev ||
                !prev.ok ||
                prev.placementV2.__typename !== 'JobPlacementV2ResponseSuccess'
              ) {
                return prev;
              }

              return {
                ...prev,
                placementV2: {
                  ...prev.placementV2,
                  result: {
                    ...prev.placementV2.result,
                    status,
                  },
                },
              };
            });
          }}
          trackJobPlacementDataSubmissionEvent={(
            optionSelected:
              | 'Yes'
              | 'No'
              | 'Not yet'
              | 'Too early to say'
              | 'Still reviewing applications',
          ) => {
            pageAnalytics.trackLink({
              event: 'option_selection_pressed',
              attributes: {
                seekListingId: jobId,
                dialogName: isJobPlacementDialogOpen.isTriggeredFromHeaderLink
                  ? 'Candidate Management - Placement - Update Hiring Status'
                  : 'Candidate Management - Placement - Hiring Check-In',
                optionSelected,
                optionSelectionType: 'radio',
                prospectInboxCount: statusFacets.inboxCount,
                prospectPrescreenCount: statusFacets.prescreenCount,
                prospectShortlistCount: statusFacets.shortlistCount,
                prospectInterviewCount: statusFacets.interviewCount,
                prospectOfferCount: statusFacets.offerCount,
                prospectAcceptCount: statusFacets.acceptCount,
                prospectNotSuitableCount: statusFacets.rejectedCount,
                hubbleExperiments: {
                  cm_improve_direct_placement_signals: {
                    name: 'cm_improve_direct_placement_signals',
                    participantStatus: 'enrolled',
                    variant: jobPlacementExperimentVariant,
                  },
                },
                experiments: [
                  {
                    num: 3,
                    id: 'cm_improve_direct_placement_signals',
                    variant: jobPlacementExperimentVariant,
                  },
                ],
              },
            });
          }}
          trackJobPlacementDialogDismissedEvent={(dialogName) => {
            pageAnalytics.trackLink({
              event: 'dialog_dismiss_pressed',
              attributes: {
                seekListingId: jobId,
                dialogName,
                hubbleExperiments: {
                  cm_improve_direct_placement_signals: {
                    name: 'cm_improve_direct_placement_signals',
                    participantStatus: 'enrolled',
                    variant: jobPlacementExperimentVariant,
                  },
                },
                experiments: [
                  {
                    num: 3,
                    id: 'cm_improve_direct_placement_signals',
                    variant: jobPlacementExperimentVariant,
                  },
                ],
              },
            });
          }}
          trackJobPlacementDialogDisplayedEvent={(dialogName) => {
            pageAnalytics.trackView({
              event: 'dialog_displayed',
              attributes: {
                seekListingId: jobId,
                dialogName,
                hubbleExperiments: {
                  cm_improve_direct_placement_signals: {
                    name: 'cm_improve_direct_placement_signals',
                    participantStatus: 'enrolled',
                    variant: jobPlacementExperimentVariant,
                  },
                },
                experiments: [
                  {
                    num: 3,
                    id: 'cm_improve_direct_placement_signals',
                    variant: jobPlacementExperimentVariant,
                  },
                ],
              },
            });
          }}
          trackJobPlacementViewReportButtonPressedEvent={() => {
            pageAnalytics.trackLink({
              event: 'view_report_pressed',
              attributes: {
                seekListingId: jobId,
                linkAction: 'Redirect to Report',
                linkText: 'View report',
                dialogName: 'Candidate Management - Placement - Improve Job Ad',
                hubbleExperiments: {
                  cm_improve_direct_placement_signals: {
                    name: 'cm_improve_direct_placement_signals',
                    participantStatus: 'enrolled',
                    variant: jobPlacementExperimentVariant,
                  },
                },
                experiments: [
                  {
                    num: 3,
                    id: 'cm_improve_direct_placement_signals',
                    variant: jobPlacementExperimentVariant,
                  },
                ],
              },
            });
          }}
          trackJobPlacementUpgradeButtonPressedEvent={() => {
            pageAnalytics.trackLink({
              event: 'job_upgrade_button_pressed',
              attributes: {
                seekListingId: jobId,
                linkAction: 'Redirect to Upgrade',
                linkText: 'Upgrade',
                dialogName:
                  "Candidate Management - Placement - Can't Find The Right Candidate",
                hubbleExperiments: {
                  cm_improve_direct_placement_signals: {
                    name: 'cm_improve_direct_placement_signals',
                    participantStatus: 'enrolled',
                    variant: jobPlacementExperimentVariant,
                  },
                },
                experiments: [
                  {
                    num: 3,
                    id: 'cm_improve_direct_placement_signals',
                    variant: jobPlacementExperimentVariant,
                  },
                ],
              },
            });
          }}
          trackJobPlacementViewHiringAdviceButtonPressedEvent={() => {
            pageAnalytics.trackLink({
              event: 'view_hiring_advice_pressed',
              attributes: {
                seekListingId: jobId,
                linkAction: 'Redirect to Article',
                linkText: 'View More Hiring Advice',
                dialogName:
                  'Candidate Management - Placement - Improve Next Job Ad',
                hubbleExperiments: {
                  cm_improve_direct_placement_signals: {
                    name: 'cm_improve_direct_placement_signals',
                    participantStatus: 'enrolled',
                    variant: jobPlacementExperimentVariant,
                  },
                },
                experiments: [
                  {
                    num: 3,
                    id: 'cm_improve_direct_placement_signals',
                    variant: jobPlacementExperimentVariant,
                  },
                ],
              },
            });
          }}
          trackJobPlacementHiringAdviceLinkPressedEvent={(elementText) => {
            pageAnalytics.trackLink({
              event: 'content_element_pressed',
              attributes: {
                seekListingId: jobId,
                elementAction: 'article link clicked',
                elementText,
                elementDesign: 'link',
                hubbleExperiments: {
                  cm_improve_direct_placement_signals: {
                    name: 'cm_improve_direct_placement_signals',
                    participantStatus: 'enrolled',
                    variant: jobPlacementExperimentVariant,
                  },
                },
                experiments: [
                  {
                    num: 3,
                    id: 'cm_improve_direct_placement_signals',
                    variant: jobPlacementExperimentVariant,
                  },
                ],
              },
            });
          }}
        />
      )}
      <NotificationPreferenceDialog
        open={queryParams.overlay === 'user-preferences'}
        onClose={() => {
          setQueryParams.clearOverlayModal();
        }}
        jobId={String(jobId)}
        jobTitle={jobStore.job.jobInformation.title}
        isJobExpired={jobStore.job.isExpired}
        seekAdvertiserId={seekAdvertiserId}
        userId={String(userStore.user.userId)}
        email={userStore.user.email}
      />
      {isMobile ? (
        <FilterDrawerMobile
          showDrawer={isFilterDrawerOpen}
          setShowDrawer={setFilterDrawerOpen}
          statusFilters={{
            statusFacets,
            onClickStatusFolder,
          }}
          screeningQuestionFilters={
            questionStore.hasQuestion
              ? {
                  content: renderScreeningQuestionFilters(),
                  selectedQuestions:
                    jobApplicationStore.filtersStore.selectedQuestionAnswers.map(
                      ({ question }) => question.statement ?? question.text,
                    ),
                }
              : null
          }
          shouldShowClearFilterButton={shouldShowClearFilterButton}
          useFiltersFns={useFiltersFns}
          locationFilter={
            canShowLocationFilter
              ? {
                  content: renderLocationFilter({
                    mobile: true,
                  }),
                  selectedLocations: filters.selectedHomeLocations.map(
                    ({ name }) => name,
                  ),
                }
              : null
          }
          nationalityFilter={
            canShowNationalityFilterInCandidateManagement
              ? {
                  content: renderNationalityFilter({
                    mobile: true,
                  }),
                  selectedNationalities: filters.selectedNationalities.map(
                    ({ name }) => name,
                  ),
                }
              : null
          }
        />
      ) : null}
    </>
  );
});
